import {Component, OnInit, TemplateRef, ViewChild} from '@angular/core';
import {combineLatest} from 'rxjs';
import {map} from 'rxjs/operators';
import {Config, ConfigType, Device} from 'smag-commons/cfg/connector';
import {MsgBoxService} from '../../shared/msg-box.service';
import {I18NService} from '../../i18n/i18n.service';
import {MatDialog} from '@angular/material/dialog';
import {NewConnector, NewConnectorComponent, NewConnectorData} from './new-connector/new-connector.component';
import {SmagApiService} from '../../api/smag-api.service';
import {SOURCES} from 'smag-commons/dto/msg';
import {can} from '../../shared/permissions';
import {Permission} from 'smag-commons/dto/api';
import {AuthorizedObject} from '../../api/dto';
import {DialogService} from '../../shared/dialog/dialog.service';
import {CONNECTABLE_CONNECTOR_TYPES} from './connectors';

const NEW_CONNECTOR_DIALOG_MIN_WIDTH = 350;

interface Connector {
    id: string;
    type: ConfigType;
}

@Component({
    selector: 'app-connectivity',
    templateUrl: './config-connectivity.component.html',
    styleUrls: ['./config-connectivity.component.scss']
})
export class ConfigConnectivityComponent implements OnInit {

    @ViewChild('updateConnector') private updateConnectorDialog: TemplateRef<any>;
    @ViewChild('deleteConnector') private deleteConnectorDialog: TemplateRef<any>;
    @ViewChild('podConnection') private podConnectionDialog: TemplateRef<any>;

    connectors: Connector[];
    connectorId: string; // currently selected connector
    cfg: AuthorizedObject<Config<Device>>; // configuration currently selected connector
    valid: boolean; // validity of currently edited configuration
    canCreateConnectors: boolean;

    // exported in HTML
    ConfigType = ConfigType;

    constructor(private readonly apiService: SmagApiService,
                private readonly msgBoxService: MsgBoxService,
                private readonly i18n: I18NService,
                private readonly dialog: MatDialog,
                private readonly dialogService: DialogService) {
        this.connectors = [];
        this.canCreateConnectors = false;
        this.clearSelection();
    }

    ngOnInit() {
        this.reload();
    }

    trackByConnectorId(index: number, connector: Connector): string {
        return connector.id;
    }

    isUpdatable(cfg: AuthorizedObject<Config<Device>>): boolean {
        return can(cfg.permissions, Permission.UPDATE);
    }

    isDeletable(cfg: AuthorizedObject<Config<Device>>): boolean {
        return can(cfg.permissions, Permission.DELETE);
    }

    onClickNewConnector() {
        this.dialog.open(NewConnectorComponent, {
            data: {
                connectors: this.connectors.map(connector => connector.id)
            } as NewConnectorData,
            minWidth: `${NEW_CONNECTOR_DIALOG_MIN_WIDTH}px`,
            hasBackdrop: true
        }).afterClosed().subscribe((newConnector: NewConnector) => {
            if (newConnector) {
                this.newConnector(newConnector.id, newConnector.config);
                this.clearSelection();
            }
        });
    }

    onClickUpdateConnectorConfig() {
        this.showPodConnectionDialog()
            .then(ok => {
                if (ok) {
                    this.dialog.open(this.updateConnectorDialog, {
                        hasBackdrop: true
                    }).afterClosed().subscribe(upd => {
                        if (upd) {
                            this.updateConnector(this.connectorId, this.cfg);
                            this.clearSelection();
                        }
                    });
                }
            });
    }

    onClickDeleteConnectorConfig() {
        this.dialog.open(this.deleteConnectorDialog, {
            hasBackdrop: true
        }).afterClosed().subscribe(del => {
            if (del) {
                this.deleteConnector(this.connectorId);
                this.clearSelection();
            }
        });
    }

    loadConnectorConfig(connectorId: string) {
        this.clearSelection();
        this.connectorId = connectorId;
        if (connectorId) {
            setTimeout(() => {
                this.apiService.getConnectorConfig(connectorId).subscribe((cfg: AuthorizedObject<Config<Device>>) => {
                    this.cfg = cfg;
                });
            }, 0);
        }
    }

    clearSelection(connectorId?: string) {
        if (!connectorId || connectorId === this.connectorId) {
            this.connectorId = null;
            this.cfg = null;
            this.valid = true;
        }
    }

    private showPodConnectionDialog(): Promise<boolean> {
        if (CONNECTABLE_CONNECTOR_TYPES.includes(this.cfg.type)) {
            return this.dialogService.open<any, boolean>({
                title: this.i18n.translate('Changes to smart-meters'),
                template: this.podConnectionDialog
            }).closed;
        }
        return Promise.resolve(true);
    }

    private newConnector(connectorId: string, config: Config<Device>) {
        this.apiService.saveConnectorConfig(connectorId, config)
            .then(() => this.reload()) // reload permissions and types
            .then(() => this.resetConnector(connectorId)) // so that connector (if already running) boots with a valid, empty config
            .then(() => {
                this.msgBoxService.info(this.i18n.translate('Connector {0} added', connectorId));
            })
            .catch(err => {
                this.msgBoxService.error(this.i18n.translate('Cannot add connector {0}', connectorId), err);
            });
    }

    private updateConnector(connectorId: string, config: Config<Device>) {
        // NOTE: no need for reload of permissions and types upon update
        this.apiService.saveConnectorConfig(connectorId, config)
            .then(() => this.resetConnector(connectorId)) // reboot connector with new config
            .then(() => {
                this.msgBoxService.info(this.i18n.translate('Connector {0} updated', connectorId));
            })
            .catch(err => {
                this.msgBoxService.error(this.i18n.translate('Cannot update connector {0}', connectorId), err);
            });
    }

    private deleteConnector(connectorId: string) {
        this.apiService.deleteConnectorConfig(connectorId)
            .then(() => this.reload()) // reload permissions and types
            .then(() => this.resetConnector(connectorId)) // so that connector (if still running) reboots with no config
            .then(() => {
                this.msgBoxService.info(this.i18n.translate('Connector {0} deleted', connectorId));
            })
            .catch(err => {
                this.msgBoxService.error(this.i18n.translate('Cannot delete connector {0}', connectorId), err);
            });
    }

    private resetConnector(connectorId: string): Promise<void> {
        return this.apiService.resetConnectors({
            connectors: [connectorId]
        }, SOURCES.MANUAL).then();
    }

    private reload() {
        combineLatest([
            this.apiService.getAllPermittedConnectors(),
            this.apiService.getConnectorTypes()
        ]).pipe(
            map(([permConnectors, connectorTypes]) => Object.keys(permConnectors)
                .filter(connectorId => can(permConnectors[connectorId], Permission.READ))
                .map(connectorId => ({
                    id: connectorId,
                    type: connectorTypes[connectorId]
                } as Connector))),
        ).subscribe(connectors => {
            this.connectors = connectors;
        });

        this.apiService.getConnectorPermissions().pipe(
            map(allConnectorsPerms => can(allConnectorsPerms.permissions, Permission.CREATE)),
        ).subscribe(canCreate => {
            this.canCreateConnectors = canCreate;
        });
    }
}
